import { useEffect, useRef } from "react";

import { isEqual } from "lodash-es";

import type { ChatEmptyStatePrompt } from "src/base-components/Chat/types";
import { useCopilotStore } from "src/unifiedCopilot/context/CopilotContext";
import {
  AmbientContextualEntitiesRegistration,
  SuggestionPromptsRegistration,
} from "src/unifiedCopilot/context/store";
import {
  AmbientContextualEntityRegistrationOptions,
  ContextualEntity,
  ReferenceContextualEntity,
  RefetchCallback,
} from "src/unifiedCopilot/context/types";

type AgentContextRegistrationOptions = {
  includeInAgentContext: true;
};

type MentionOnlyRegistrationOptions = {
  includeInAgentContext?: false;
};

type UseCopilotContextualEntities = {
  (
    entities: ReferenceContextualEntity[],
    options?: MentionOnlyRegistrationOptions,
  ): void;
  (
    entities: ContextualEntity[],
    options: AgentContextRegistrationOptions,
  ): void;
};

/**
 * Declares the ambient contextual entities this page/component provides.
 *
 * Declarative and mount-scoped: The entities live in the registry while this component is mounted, and are removed on unmount.
 * Reference entities are mentionable by default. Use
 * `includeInAgentContext: true` to also send entities to the agent
 * automatically on every turn. When multiple ambient providers register the
 * same entity, any provider opting in is sufficient for automatic inclusion.
 * Use `useCopilotContextualEntityImperative` instead when the context should live beyond the component's lifetime.
 */
export const useCopilotContextualEntities: UseCopilotContextualEntities = (
  entities: ContextualEntity[],
  options: AmbientContextualEntityRegistrationOptions = {},
): void => {
  const store = useCopilotStore();
  const includeInAgentContext = options.includeInAgentContext ?? false;
  // Use a single registration for the lifetime of this hook
  const registrationRef = useRef<AmbientContextualEntitiesRegistration | null>(
    null,
  );
  // Keep track of the last entities we put
  const lastPutEntitiesRef = useRef<ContextualEntity[] | null>(null);

  useEffect(() => {
    // If nothing changed we break early
    if (
      lastPutEntitiesRef.current !== null &&
      isEqual(lastPutEntitiesRef.current, entities)
    ) {
      return;
    }
    // Get a registration if we don't have one yet
    registrationRef.current ??= store
      .getState()
      .actions.createAmbientContextualEntitiesRegistration({
        includeInAgentContext,
      });

    // Put the entities into the registration and remember them
    registrationRef.current.put(entities);
    lastPutEntitiesRef.current = entities;
  }, [store, entities, includeInAgentContext]);

  useEffect(
    // Cleanup on unmount
    () => () => {
      registrationRef.current?.dispose();
      // Cleanup also runs while the instance survives (StrictMode's simulated
      // unmount, `store` change)
      registrationRef.current = null;
      lastPutEntitiesRef.current = null;
    },
    [store, includeInAgentContext],
  );
};

type SetCopilotContextualEntities = {
  (
    entities: ReferenceContextualEntity[],
    options?: MentionOnlyRegistrationOptions,
  ): () => void;
  (
    entities: ContextualEntity[],
    options: AgentContextRegistrationOptions,
  ): () => void;
};

export type CopilotContextualEntityActions = {
  set: SetCopilotContextualEntities;
};

/**
 * Imperative ambient registration for event-driven context like editor
 * selections.
 *
 * Purely imperative: entities registered through `set` stay in the registry
 * until the returned remover is called. Unmounting the calling component does
 * NOT remove them. Use `useCopilotContextualEntities` instead when the
 * context should live and die with the component.
 */
export const useCopilotContextualEntityImperative =
  (): CopilotContextualEntityActions => {
    const store = useCopilotStore();

    return {
      set: (
        entities: ContextualEntity[],
        options: AmbientContextualEntityRegistrationOptions = {},
      ) => {
        const registration = store
          .getState()
          .actions.createAmbientContextualEntitiesRegistration({
            includeInAgentContext: options.includeInAgentContext ?? false,
          });
        registration.put(entities);
        return registration.dispose;
      },
    };
  };

/**
 * Runs `refetch` after each agent tool call that may have mutated backend
 * state. Registered while mounted,
 * removed on unmount. The latest callback from the most recent render is the
 * one invoked; no dependency array needed.
 */
export const useCopilotRefetchOnMutation = (refetch: RefetchCallback): void => {
  const store = useCopilotStore();
  const refetchRef = useRef<RefetchCallback>(refetch);

  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  useEffect(() => {
    const unregister = store
      .getState()
      .actions.registerRefetchCallback(refetchRef);
    return unregister;
  }, [store]);
};

/**
 * Declares the empty-state suggestions this surface offers in the copilot,
 * the counterpart of `useCopilotContextualEntities` for prompts.
 *
 * Declarative and mount-scoped: the prompts show while this component is
 * mounted and are withdrawn on unmount. With several surfaces mounted, their
 * prompts are listed in registration order; without any, the chat shows its
 * defaults.
 */
export const useCopilotSuggestionPrompts = (
  prompts: ChatEmptyStatePrompt[],
): void => {
  const store = useCopilotStore();
  // Use a single registration for the lifetime of this hook
  const registrationRef = useRef<SuggestionPromptsRegistration | null>(null);
  // Keep track of the last prompts we put
  const lastPutPromptsRef = useRef<ChatEmptyStatePrompt[] | null>(null);

  useEffect(() => {
    // If nothing changed we break early
    if (
      lastPutPromptsRef.current !== null &&
      isEqual(lastPutPromptsRef.current, prompts)
    ) {
      return;
    }
    registrationRef.current ??= store
      .getState()
      .actions.createSuggestionPromptsRegistration();

    registrationRef.current.put(prompts);
    lastPutPromptsRef.current = prompts;
  }, [store, prompts]);

  useEffect(
    // Cleanup on unmount
    () => () => {
      registrationRef.current?.dispose();
      // Cleanup also runs while the instance survives (StrictMode's simulated
      // unmount, `store` change)
      registrationRef.current = null;
      lastPutPromptsRef.current = null;
    },
    [store],
  );
};
